import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styles from './MicrositeSeoPageEditor.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import { MicrositeUnsavedTabsContext } from '../MicrositeShell/MicrositeShell'
import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import TextInput from '../components/TextInput/TextInput'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeSlider from '../components/MicrositeSlider/MicrositeSlider'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeSerpPreview from '../components/MicrositeSerpPreview/MicrositeSerpPreview'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const CHANGE_FREQUENCY_OPTIONS = [
    { value: 'always', label: 'Always' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'never', label: 'Never' }
]

const EMPTY_FORM = {
    pageSlug: '',
    pageTitle: '',
    pageDescription: '',
    ogImage: '',
    noindex: false,
    nofollow: false,
    priority: 0.5,
    changeFrequency: 'monthly'
}

/**
 * MicrositeSeoPageEditor
 * Dedicated edit screen for a single per-page SEO override.
 * Reached via /microsite/seo/pages/:id/edit, or /microsite/seo/pages/new
 * for creating a new override (id === 'new').
 */
const MicrositeSeoPageEditor = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const isNew = id === 'new' || !id

    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])
    const { micrositeStore } = useContext(MicrositeUnsavedTabsContext)

    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    // Note: there's no dedicated "get single SEO page" API in the
    // current contract — micrositeGetSeoPages returns a list. For
    // an existing record, we fetch the list and find the match by
    // ID client-side. If the list grows large this should get a
    // dedicated fetch-by-ID endpoint server-side.
    const loadExisting = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetSeoPages', { limit: 100 })
        if (res?.status === 'success') {
            const match = (res.response?.data ?? []).find((r) => String(r.ID) === String(id))
            if (match) {
                const next = {
                    pageSlug: match.pageSlug || '',
                    pageTitle: match.pageTitle || '',
                    pageDescription: match.pageDescription || '',
                    ogImage: match.ogImage || '',
                    noindex: !!match.noindex,
                    nofollow: !!match.nofollow,
                    priority: match.priority ?? 0.5,
                    changeFrequency: match.changeFrequency || 'monthly'
                }
                setForm(next)
                setOriginal(next)
            } else {
                showToast('Could not find this page override', 'error')
            }
        } else {
            showToast('Could not load page override. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post, id])

    useEffect(() => {
        if (!isNew && activeStore?.storeIdentifier) loadExisting()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, id])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleSave = async () => {
        if (!form.pageSlug.trim()) {
            showToast('Page slug is required', 'error')
            return
        }
        setSaving(true)
        const res = await post('micrositeUpsertSeoPage', { ...form })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(form)
            showToast('Page SEO saved successfully')
            navigate('/microsite/seo/pages')
        } else {
            showToast('Could not save. Please try again.', 'error')
        }
    }

    const handleDelete = async () => {
        const res = await post('micrositeDeleteSeoPage', { ID: id })
        if (res?.status === 'success') {
            showToast('Page override deleted')
            navigate('/microsite/seo/pages')
        } else {
            showToast('Could not delete', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading page override…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title={isNew ? 'New page SEO override' : 'Edit page SEO'}
                backTo="/microsite/seo/pages"
                backLabel="Page SEO"
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onDelete={!isNew ? () => setDeleteConfirmOpen(true) : undefined}
            />

            <div className={styles.layout}>
                <div className={styles.formCol}>
                    <section className={styles.fieldGroup}>
                        <TextInput
                            label="Page slug"
                            value={form.pageSlug}
                            onChange={(v) => update('pageSlug', v)}
                            placeholder="/about-us"
                            required
                            disabled={!isNew}
                            helperText={!isNew ? 'Slug cannot be changed after creation' : undefined}
                        />
                        <TextInput
                            label="Page title"
                            value={form.pageTitle}
                            onChange={(v) => update('pageTitle', v)}
                            placeholder="About Us — Glam Studio"
                            maxLength={60}
                            showCharCount
                        />
                        <TextInput
                            label="Page description"
                            value={form.pageDescription}
                            onChange={(v) => update('pageDescription', v)}
                            placeholder="A short description for this specific page"
                            maxLength={160}
                            showCharCount
                        />
                        <TextInput
                            label="Open Graph image URL"
                            value={form.ogImage}
                            onChange={(v) => update('ogImage', v)}
                            placeholder="https://example.com/page-share-image.jpg"
                        />
                    </section>

                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Indexing & sitemap</h2>
                        <ToggleSwitch
                            label="Hide from search engines (noindex)"
                            description="Search engines won't include this page in results"
                            checked={form.noindex}
                            onChange={(v) => update('noindex', v)}
                        />
                        <div className={styles.toggleSpacer} />
                        <ToggleSwitch
                            label="Don't follow links on this page (nofollow)"
                            description="Search engines won't follow links found on this page"
                            checked={form.nofollow}
                            onChange={(v) => update('nofollow', v)}
                        />
                        <MicrositeSlider
                            label="Sitemap priority"
                            value={form.priority}
                            onChange={(v) => update('priority', v)}
                        />
                        <SelectInput
                            label="Change frequency"
                            value={form.changeFrequency}
                            onChange={(v) => update('changeFrequency', v)}
                            options={CHANGE_FREQUENCY_OPTIONS}
                        />
                    </section>
                </div>

                <div className={styles.previewCol}>
                    <div className={styles.previewSticky}>
                        <MicrositeSerpPreview
                            title={form.pageTitle}
                            description={form.pageDescription}
                            url={`${micrositeStore?.subdomain || 'yoursalon'}.lokacipro.com${form.pageSlug}`}
                        />
                    </div>
                </div>
            </div>

            <MicrositeDeleteConfirm
                mode="modal"
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
                itemLabel="this page SEO override"
            />
        </div>
    )
}

export default MicrositeSeoPageEditor